import { createSelector } from '@reduxjs/toolkit';
import { cloneDeep } from 'es-toolkit';
import ms from 'ms';
import { REHYDRATE } from 'redux-persist/constants';

import api from '@/redux/api/recent';
import { getAuthToken } from '@/redux/modules/account/user/user.selectors';
import { AppDispatch, AppGetState, GlobalState } from '@/redux/store';

import { getDeployment } from './config';

/**
 * Normalizes a timestamp to milliseconds.
 * API returns timestamps in seconds (10 digits), but client-side uses milliseconds (13 digits).
 * This ensures consistent sorting regardless of timestamp source.
 */
export const normalizeTimestampToMs = (timestamp: number): number => {
    // Timestamps before 10000000000 (year 2286 in seconds, or April 1970 in ms) are in seconds
    return timestamp < 10000000000 ? timestamp * 1000 : timestamp;
};

/* Action Types */
const LOAD_RECENT_ITEMS_FAIL = 'la/domain/recentItems/LOAD_FAIL';
const LOAD_RECENT_ITEMS_REQUEST = 'la/domain/recentItems/LOAD_REQUEST';
const LOAD_RECENT_ITEMS_SUCCESS = 'la/domain/recentItems/LOAD_SUCCESS';

const REDUX_STORE_TIME = ms('30m');

/* reducer */
export type RecentItemSlice = {
    byId: {};
    loaded: number;
    loading: boolean;
};

export const defaultRecentItemSlice: RecentItemSlice = {
    byId: {},
    loaded: 0,
    loading: false,
};

export default function reducer(state: RecentItemSlice = defaultRecentItemSlice, action: any = {}): RecentItemSlice {
    let existing;
    let time;

    switch (action.type) {
        case REHYDRATE:
            if (Object.prototype.hasOwnProperty.call(action.payload, 'recentItem')) {
                const incoming = action.payload.recentItem;
                // Merge localStorage data with existing state (from SSR/API), keeping the most recent timestamp for each item
                const mergedById = cloneDeep(state.byId);

                if (incoming.byId) {
                    Object.keys(incoming.byId).forEach((itemId) => {
                        const incomingItem = incoming.byId[itemId];
                        const normalizedTimestamp = incomingItem.timestamp
                            ? normalizeTimestampToMs(incomingItem.timestamp)
                            : 0;

                        const existingItem = mergedById[itemId];
                        // Keep whichever timestamp is more recent
                        if (!existingItem || normalizedTimestamp > existingItem.timestamp) {
                            mergedById[itemId] = { ...incomingItem, timestamp: normalizedTimestamp };
                        }
                    });
                }

                return {
                    ...state,
                    ...incoming,
                    byId: mergedById,
                    // Keep loaded time from SSR if it's more recent, otherwise localStorage wins
                    loaded: Math.max(state.loaded || 0, incoming.loaded || 0),
                };
            }

            return state;
        case LOAD_RECENT_ITEMS_FAIL:
            return {
                ...state,
                loading: false,
            };
        case LOAD_RECENT_ITEMS_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case LOAD_RECENT_ITEMS_SUCCESS:
            existing = cloneDeep(state.byId);
            time = action.meta.actionTime;

            if (action.payload.recentItems) {
                action.payload.recentItems.forEach((x) => {
                    // Normalize API timestamps (seconds) to milliseconds for consistent sorting
                    existing[x.itemId] = { ...x, timestamp: normalizeTimestampToMs(x.timestamp) };
                });
            }

            return {
                ...state,
                byId: existing,
                loaded: time,
                loading: false,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state;
const recentItemSlice = createSelector(stateSelector, (state) => state.recentItem);

const idSelector = (_: GlobalState, id: number) => id;

const byIdSelector = createSelector(recentItemSlice, (state) => state.byId);

const getLoadTimeForRecentItems = createSelector(recentItemSlice, (state) => state.loaded);

export const isRecentItemsLoading = createSelector(recentItemSlice, (state) => state.loading);

const getSortedRecentItemIdsFromState = createSelector(byIdSelector, (byId) => {
    const toBeSorted = Object.keys(byId).map((item) => byId[item]);
    const sorted = toBeSorted.sort((a, b) => b.timestamp - a.timestamp);
    return sorted.map((item): number => item.itemId);
});

export const getLimitedSortedRecentItemIdsFromState = createSelector(
    [getSortedRecentItemIdsFromState, idSelector],
    (sortedRecentItemIds, limit) => {
        // prevent 0's from being returned which happens sometimes
        return sortedRecentItemIds.filter((id) => id).slice(0, limit);
    }
);

export const getViewTime = createSelector([byIdSelector, idSelector], (byId, id) => {
    const x = byId[id];
    return x && x.timestamp ? x.timestamp : 0;
});

const shouldFetchRecentItems = createSelector([getLoadTimeForRecentItems, isRecentItemsLoading], (loaded, loading) => {
    const time = Date.now();
    const diff = time - loaded;
    if (diff < REDUX_STORE_TIME) {
        return false;
    }
    return !loading;
});

/* ACTION CREATORS */
const fetchRecentItems = () => async (dispatch: AppDispatch, getState: AppGetState) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        dispatch({
            type: LOAD_RECENT_ITEMS_REQUEST,
        });
        const response = await api.fetchRecentItems({
            authToken,
            deployment,
        });
        dispatch({
            meta: { actionTime: Date.now() },
            payload: response.data,
            type: LOAD_RECENT_ITEMS_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: LOAD_RECENT_ITEMS_FAIL,
        });
    }
};

export const fetchRecentItemsIfNeeded = () => async (dispatch: AppDispatch, getState: AppGetState) => {
    const needed = shouldFetchRecentItems(getState());
    if (needed) {
        return dispatch(fetchRecentItems());
    }
    return Promise.resolve();
};
